/**
 * @file bootstrap-public-config.ts
 * @description Fetches the public URL bundle from the backend and populates
 * `window.ENV.VITE_PRODGY_*_URL` BEFORE the React tree mounts. The backend is
 * the single source of truth for these URLs (env-driven via `PRODGY_DOCS_URL`)
 * — the frontend Container App no longer carries `VITE_PRODGY_DOCS_URL` /
 * `VITE_PRODGY_TERMS_URL` / `VITE_PRODGY_PRIVACY_URL` env vars.
 *
 * Failure-tolerant: if the call fails (api down at boot, network blip), the
 * window.ENV values stay empty and the existing `getEnvLocalizedUrl` helper
 * already falls back to `'#'`. The app still renders.
 */

import { API_CONFIG } from "./api";

interface PublicUrlsResponse {
  docsUrl?: string;
  termsUrl?: string;
  privacyUrl?: string;
}

const ENDPOINT_PATH = "/config/public-urls";
const FETCH_TIMEOUT_MS = 5000;

export async function loadPublicConfig(): Promise<void> {
  // Roda ANTES da React tree montar, e nao depende mais de flag: a rota vive na
  // raiz da api (sem o prefixo /api), que e o que `API_ROOT_URL` aponta. Antes
  // isto consultava a flag `api-v2-auth` e caia em legacy na primeira carga, sem
  // cache — removido junto com a flag (AB#81972).
  const url = `${API_CONFIG.API_ROOT_URL}${ENDPOINT_PATH}`;

  try {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      FETCH_TIMEOUT_MS,
    );

    const response = await fetch(url, { signal: controller.signal });
    window.clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn("[bootstrap] /config/public-urls returned", response.status);
      return;
    }

    const body = (await response.json()) as PublicUrlsResponse;

    // index.html always seeds window.ENV; the bracket access narrows the type
    // for the assignments below.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = ((window as any).ENV ??= {}) as Record<string, string>;
    if (body.docsUrl) env.VITE_PRODGY_DOCS_URL = body.docsUrl;
    if (body.termsUrl) env.VITE_PRODGY_TERMS_URL = body.termsUrl;
    if (body.privacyUrl) env.VITE_PRODGY_PRIVACY_URL = body.privacyUrl;
  } catch (err) {
    console.warn(
      "[bootstrap] Failed to load public config — fallbacks will apply",
      err,
    );
  }
}
